import React, { useState, createContext, useContext } from 'react';
import { addDays, format, isWithinInterval, parseISO, startOfDay } from 'date-fns';
export type Villa = {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  image: string;
  images: string[];
  sleeps: number;
  bedrooms: number;
  bathrooms: number;
  nightlyRate: number;
  amenities: string[];
  testimonial: {
    text: string;
    author: string;
  };
};
export type BookingStatus = 'Confirmed' | 'Pending';
export type BookingSource = 'Direct' | 'Agent' | 'OTA';
export type Booking = {
  id: string;
  villaId: string;
  guestName: string;
  guestEmail: string;
  checkIn: string; // ISO string
  checkOut: string; // ISO string
  status: BookingStatus;
  source: BookingSource;
};
interface BookingContextType {
  villas: Villa[];
  bookings: Booking[];
  addBooking: (booking: Omit<Booking, 'id'>) => Booking;
  simulateOTABooking: () => void;
  isDateAvailable: (villaId: string, date: Date) => boolean;
  getBookingForDate: (villaId: string, date: Date) => Booking | undefined;
}
const initialVillas: Villa[] = [{
  id: 'v1',
  name: 'Sunset Oasis',
  shortDescription: 'Elegant 3-bedroom villa with sunset views and private pool.',
  description: 'Elegant 3-bedroom pool villa with exceptional sunset views. Includes private swimming pool, garden, full kitchen, outdoor dining, and luxury lounging spaces.',
  image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80',
  images: ['https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80'],
  sleeps: 6,
  bedrooms: 3,
  bathrooms: 3,
  nightlyRate: 400,
  amenities: ['WiFi', 'Air Conditioning', 'Smart TV', 'BBQ', 'Safe'],
  testimonial: {
    text: 'The perfect family getaway! Highly recommended.',
    author: 'Jane D.'
  }
}, {
  id: 'v2',
  name: 'Emerald Retreat',
  shortDescription: 'Spacious family villa, close to the beach, luxury amenities.',
  description: 'Spacious family villa located just steps from the beach. Features lush gardens, a large private pool, and premium luxury amenities for the ultimate relaxation.',
  image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80',
  images: ['https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80'],
  sleeps: 8,
  bedrooms: 4,
  bathrooms: 4,
  nightlyRate: 550,
  amenities: ['WiFi', 'Air Conditioning', 'Beach Access', 'Chef Service', 'Pool'],
  testimonial: {
    text: 'Absolutely stunning property and incredible service.',
    author: 'Mark L.'
  }
}, {
  id: 'v3',
  name: 'Palm Springs Villa',
  shortDescription: 'Modern design, infinity pool, and fully equipped kitchen.',
  description: "A masterpiece of modern design featuring an infinity pool overlooking the valley, a fully equipped chef's kitchen, and expansive open-plan living areas.",
  image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1200&q=85',
  images: ['https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1200&q=85', 'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?auto=format&fit=crop&w=1200&q=85'],
  sleeps: 4,
  bedrooms: 2,
  bathrooms: 2,
  nightlyRate: 350,
  amenities: ['WiFi', 'Infinity Pool', 'Modern Kitchen', 'Gym Access'],
  testimonial: {
    text: 'Modern, clean, and breathtaking views.',
    author: 'Sarah K.'
  }
}, {
  id: 'v4',
  name: 'Azure Escape',
  shortDescription: 'Tranquil retreat with panoramic views and private deck.',
  description: 'Find your peace in this tranquil retreat offering panoramic ocean views from a private wooden deck. Perfect for couples seeking a romantic getaway.',
  image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=800&q=80',
  images: ['https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?auto=format&fit=crop&w=800&q=80'],
  sleeps: 2,
  bedrooms: 1,
  bathrooms: 1,
  nightlyRate: 250,
  amenities: ['WiFi', 'Ocean View', 'Private Deck', 'Hot Tub'],
  testimonial: {
    text: 'A romantic paradise. We will definitely be back.',
    author: 'Emily R.'
  }
}];
const initialBookings: Booking[] = [{
  id: 'b1',
  villaId: 'v1',
  guestName: 'Mark Lee',
  guestEmail: 'mark@example.com',
  checkIn: startOfDay(addDays(new Date(), 2)).toISOString(),
  checkOut: startOfDay(addDays(new Date(), 5)).toISOString(),
  status: 'Confirmed',
  source: 'Agent'
}, {
  id: 'b2',
  villaId: 'v2',
  guestName: 'Sarah Kim',
  guestEmail: 'sarah@example.com',
  checkIn: startOfDay(addDays(new Date(), 7)).toISOString(),
  checkOut: startOfDay(addDays(new Date(), 10)).toISOString(),
  status: 'Pending',
  source: 'Agent'
}];
const BookingContext = createContext<BookingContextType | undefined>(undefined);
export const BookingProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [villas] = useState<Villa[]>(initialVillas);
  const [bookings, setBookings] = useState<Booking[]>(initialBookings);
  const addBooking = (bookingData: Omit<Booking, 'id'>) => {
    const newBooking: Booking = {
      ...bookingData,
      id: `b${Date.now()}`
    };
    setBookings(prev => [...prev, newBooking]);
    return newBooking;
  };
  const simulateOTABooking = () => {
    // Simulate a booking on Azure Escape starting tomorrow for 3 days
    const checkInDate = startOfDay(addDays(new Date(), 1));
    const checkOutDate = startOfDay(addDays(checkInDate, 3));
    addBooking({
      villaId: 'v4',
      guestName: 'Agoda Guest',
      guestEmail: 'guest@agoda.com',
      checkIn: checkInDate.toISOString(),
      checkOut: checkOutDate.toISOString(),
      status: 'Confirmed',
      source: 'OTA'
    });
  };
  const getBookingForDate = (villaId: string, date: Date) => {
    const targetDate = startOfDay(date);
    return bookings.find(b => {
      if (b.villaId !== villaId) return false;
      const start = startOfDay(parseISO(b.checkIn));
      const end = startOfDay(parseISO(b.checkOut));
      // Date is booked if it's >= checkIn and < checkOut
      return targetDate >= start && targetDate < end;
    });
  };
  const isDateAvailable = (villaId: string, date: Date) => {
    return getBookingForDate(villaId, date) === undefined;
  };
  return <BookingContext.Provider value={{
    villas,
    bookings,
    addBooking,
    simulateOTABooking,
    isDateAvailable,
    getBookingForDate
  }} data-id="element-49" data-mp="1o0ihrxucngsf">
      {children}
    </BookingContext.Provider>;
};
export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};